/**
 * Asset Filter Configuration
 *
 * Filter dropdown widths and filter-specific settings.
 * NO JSX - pure TypeScript only.
 *
 * Pattern matches:
 * - src/sections/asset/config/columns.ts (column widths)
 * - src/sections/seeds/config/filters.ts (filter widths)
 */

// ============================================================================
// FILTER DROPDOWN WIDTH CONFIGURATION
// ============================================================================

/**
 * Filter dropdown width configuration for asset filters
 *
 * Defines custom widths for filters that need more space to display full text.
 * Filters not listed here will use the default width (400px).
 *
 * Available asset filters (from entityFilters.util.ts):
 * - asname: AS Name
 * - asnumber: AS Number
 * - attackSurface: Attack Surface
 * - class: IP Version
 * - country: Country
 * - domain: Domain
 * - new: Recent
 * - origins: Origin
 * - port: Port Number
 * - protocol: Protocol
 * - source: Source
 * - status: Status
 * - tags: Tag
 *
 * @example
 * ```tsx
 * const width = FILTER_DROPDOWN_WIDTHS.asname; // 500
 * const defaultWidth = FILTER_DROPDOWN_WIDTHS.default; // 400
 * ```
 */
export const FILTER_DROPDOWN_WIDTHS = {
  /** Compact dropdown for attack surface */
  attackSurface: 200,
  /** Wide dropdown for AS Name (e.g., "AMAZON-02 - Amazon.com, Inc.") */
  asname: 400,
  /** Compact dropdown for AS Number */
  asnumber: 200,
  /** Compact dropdown for IP Version (IPv4/IPv6) */
  class: 200,
  /** Medium dropdown for country names */
  country: 200,
  /** Wide dropdown for domain names */
  domain: 500,
  /** Compact dropdown for recent filter */
  new: 200,
  /** Medium dropdown for origins */
  origins: 300,
  /** Compact dropdown for port numbers */
  port: 200,
  /** Compact dropdown for protocols */
  protocol: 300,
  /** Compact dropdown for status */
  status: 200,
  /** Medium dropdown for tags */
  tags: 400,
  /** Default width for all other filters */
  default: 400,
} as const;

/**
 * Get dropdown width for an asset filter field
 *
 * @param fieldName - The filter field name
 * @returns The configured width or default width
 *
 * @example
 * ```tsx
 * const width = getFilterDropdownWidth('registrar'); // 600
 * const width = getFilterDropdownWidth('status'); // 400 (default)
 * ```
 */
export const getFilterDropdownWidth = (fieldName: string): number => {
  return (
    FILTER_DROPDOWN_WIDTHS[fieldName as keyof typeof FILTER_DROPDOWN_WIDTHS] ||
    FILTER_DROPDOWN_WIDTHS.default
  );
};
